/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Role } from "~/generated/graphql";

export type SecretState = {
  open: boolean;
  keyId: string;
  keySecret: string;
};

export type State = {
  description: string;
  expirationDate: string;
  expires: boolean;
  role: Role;
  applications: string[];
  showApps: boolean;
  showAppSidebar: boolean;
};

export enum SECRET_ACTION {
  OPEN = "OPEN",
  CLOSE = "CLOSE",
}

export enum ACTION {
  DESCRIPTION = "DESCRIPTION",
  ROLE = "ROLE",
  APPS = "APPS",
  EXPIRATION = "EXPIRATION",
  APP_SELECTION = "APP_SELECTION",
  SET = "SET",
  CLEAR = "CLEAR",
}

type SecretAction =
  | { type: SECRET_ACTION.CLOSE }
  | { type: SECRET_ACTION.OPEN; payload: { keyId: string; keySecret: string } };

export type Action =
  | { type: ACTION.DESCRIPTION; description: string }
  | { type: ACTION.EXPIRATION; expirationDate: string; expires: boolean }
  | { type: ACTION.ROLE; role: Role }
  | { type: ACTION.APPS; applications: string[] }
  | { type: ACTION.APP_SELECTION; show: boolean }
  | { type: ACTION.CLEAR }
  | {
      type: ACTION.SET;
      payload: { description: string; role: Role; applications: string[] };
    };

const showApps = (role: Role) => role === Role.TeamLead;

export const initialState: State = {
  description: "",
  role: Role.ReadOnly,
  applications: [],
  expirationDate: "",
  expires: false,
  showApps: false,
  showAppSidebar: false,
};

export const initialSecretState: SecretState = {
  open: false,
  keyId: "",
  keySecret: "",
};

export const secretReducer = (
  secretState: SecretState,
  secretAction: SecretAction
) => {
  switch (secretAction.type) {
    case SECRET_ACTION.OPEN:
      return { open: true, ...secretAction.payload };
    case SECRET_ACTION.CLOSE:
      return { open: false, keyId: "", keySecret: "" };
    default:
      throw new Error(`Unknown action`);
  }
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ACTION.SET:
      return {
        ...action.payload,
        expirationDate: "",
        expires: false,
        showAppSidebar: false,
        showApps: showApps(action.payload.role),
      };
    case ACTION.DESCRIPTION:
      return {
        ...state,
        description: action.description,
      };
    case ACTION.EXPIRATION:
      return {
        ...state,
        expirationDate: action.expires ? action.expirationDate : "",
        expires: action.expires,
      };
    case ACTION.ROLE:
      return {
        ...state,
        role: action.role,
        showApps: showApps(action.role),
      };
    case ACTION.APPS:
      return {
        ...state,
        applications: action.applications,
      };
    case ACTION.APP_SELECTION:
      return {
        ...state,
        showAppSidebar: action.show,
      };
    case ACTION.CLEAR:
      return initialState;
    default:
      throw new Error(`Unknown action`);
  }
};
